import React,{useEffect,useMemo,useState} from 'react';
import {Link,useNavigate,useParams} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {getShopUrl} from '../services/shopLinkService';
import {getOfferingPriceLabel} from '../services/offeringPricingService';
import {fetchSellerReviews} from '../services/sellerReviewService';
import {format} from 'date-fns';
import OrderFormModal from '../components/OrderFormModal';
import PolicyDocument from '../components/policies/PolicyDocument';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from '../components/common/ImageWithFallback';
import * as FiIcons from 'react-icons/fi';
import * as GiIcons from 'react-icons/gi';

const {
  FiArrowLeft,
  FiMapPin,
  FiMessageCircle,
  FiShoppingBag,
  FiStar,
  FiTruck,
  FiClock,
  FiShield,
  FiCalendar,
  FiExternalLink,
  FiLoader
}=FiIcons;
const {GiChefToque}=GiIcons;

const formatAllergenLabels = (allergens) => (
  String(allergens || '')
    .split(',')
    .map((allergen) => allergen.trim())
    .filter(Boolean)
);

const SellerProfileLoading=()=>{
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="text-center">
        <SafeIcon
          icon={FiLoader}
          className="mx-auto mb-5 text-4xl text-indigo-600 animate-spin"
        />
        <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
          Loading seller profile...
        </p>
      </div>
    </div>
  );
};

const SellerProfilePage=()=>{
  const {id,username}=useParams();
  const navigate=useNavigate();
  const {user}=useSupabaseAuth();
  const {sellers,offerings,reviews,isDataReady}=useSupabaseData();
  const [selectedOffering,setSelectedOffering]=useState(null);
  const [activeTab,setActiveTab]=useState('menu');
  const [profileReviews,setProfileReviews]=useState([]);
  const [hasLoadedProfile,setHasLoadedProfile]=useState(false);

  const seller=useMemo(
    ()=>id
      ? sellers.find((item)=>item.id===id)
      : sellers.find(
        (item)=>item.shop_username?.toLowerCase()===username?.toLowerCase()
      ),
    [id,username,sellers]
  );

  const shopUrl=getShopUrl(seller);

  const sellerOfferings=useMemo(
    ()=>offerings.filter((item)=>item.seller_id===seller?.id),
    [offerings,seller?.id]
  );

  const contextSellerReviews=useMemo(
    ()=>reviews.filter((review)=>String(review.seller_id)===String(seller?.id)),
    [reviews,seller?.id]
  );

  useEffect(()=>{
    if (isDataReady) {
      setHasLoadedProfile(true);
    }
  },[isDataReady]);

  useEffect(()=>{
    let isCurrent=true;

    if (!seller?.id) {
      setProfileReviews([]);
      return undefined;
    }

    setProfileReviews(contextSellerReviews);

    fetchSellerReviews(seller.id)
      .then((sellerReviews)=>{
        if (isCurrent) setProfileReviews(sellerReviews);
      })
      .catch((error)=>{
        console.error('Seller reviews request failed:',error);
        if (isCurrent) setProfileReviews(contextSellerReviews);
      });

    return ()=>{
      isCurrent=false;
    };
  },[seller?.id,contextSellerReviews]);

  const sellerReviews=profileReviews;

  const reviewRating=useMemo(()=>{
    if (!sellerReviews.length) return 0;

    const total=sellerReviews.reduce(
      (sum,review)=>sum+Number(review.rating || 0),
      0
    );

    return total/sellerReviews.length;
  },[sellerReviews]);

  const startConversation=()=>{
    if (!user) {
      navigate('/login',{state:{from:window.location.pathname}});
      return;
    }

    navigate(`/chat/${seller.user_id}`);
  };

  const startOrder=(offering)=>{
    if (!user) {
      navigate('/login',{
        state:{
          from:window.location.pathname
        }
      });
      return;
    }

    setSelectedOffering(offering);
  };

  if (!isDataReady && !hasLoadedProfile) {
    return <SellerProfileLoading />;
  }

  if (!seller) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="text-center">
          <h1 className="text-3xl font-black text-slate-900">
            Seller not found
          </h1>
          <Link
            to="/sellers"
            className="mt-6 inline-flex items-center font-black text-indigo-600"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/sellers"
          className="inline-flex items-center text-xs font-black uppercase tracking-widest text-slate-400 hover:text-indigo-600"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2" />
          Marketplace
        </Link>

        <motion.section
          initial={{opacity:0,y:16}}
          animate={{opacity:1,y:0}}
          className="mt-8 overflow-hidden rounded-[3rem] border border-slate-100 bg-white shadow-xl shadow-slate-200/40"
        >
          <div className="grid md:grid-cols-2">
            <ImageWithFallback
              src={
                seller.profile_image
                || seller.profile_images?.[0]
              }
              alt={seller.business_name}
              className="h-full min-h-[320px] w-full object-cover"
              fallbackLabel="No seller photo"
              fallbackIcon={GiChefToque}
            />

            <div className="flex flex-col justify-center p-10 md:p-14">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-indigo-600">
                {seller.category || 'Local Seller'}
              </p>

              <h1 className="mt-4 text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                {seller.business_name}
              </h1>

              <div className="mt-5 flex items-center text-sm font-black text-indigo-600">
                <SafeIcon icon={FiStar} className="mr-2 fill-indigo-600" />
                {reviewRating > 0
                  ? reviewRating.toFixed(1)
                  : 'New'}
                {' · '}
                {sellerReviews.length} reviews
              </div>

              <p className="mt-6 text-lg font-medium leading-relaxed text-slate-500">
                {seller.description}
              </p>

              <div className="mt-6 grid grid-cols-1 gap-3 text-sm font-bold text-slate-500 sm:grid-cols-2">
                {seller.location && (
                  <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4">
                    <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-indigo-700">
                      <SafeIcon icon={FiMapPin} />
                      Location
                    </p>
                    <p className="flex items-center">
                      <SafeIcon icon={FiMapPin} className="mr-2 text-indigo-600" />
                      {seller.location}
                    </p>
                  </div>
                )}

                {seller.availability && (
                  <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-4">
                    <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                      <SafeIcon icon={FiClock} />
                      Business hours
                    </p>
                    <p className="flex items-center">
                      <SafeIcon icon={FiClock} className="mr-2 text-indigo-600" />
                      {seller.availability}
                    </p>
                  </div>
                )}

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-emerald-700">
                    <SafeIcon icon={FiTruck} />
                    Delivery
                  </p>
                  <p className="flex items-center">
                    <SafeIcon icon={FiTruck} className="mr-2 text-indigo-600" />
                    {seller.is_delivery_enabled
                      ? `Delivery available · $${Number(
                      seller.delivery_charge || 0
                    ).toFixed(2)}`
                      : 'Pickup only'}
                  </p>
                </div>

                <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-4">
                  <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-violet-700">
                    <SafeIcon icon={FiCalendar} />
                    Minimum notice
                  </p>
                  <p className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="mr-2 text-indigo-600" />
                    {Number(seller.min_order_window || 0)>0
                    ? `${seller.min_order_window} day${
                      Number(seller.min_order_window)===1 ? '' : 's'
                    } minimum notice`
                    : 'Same-day orders available'}
                  </p>
                </div>

                {shopUrl && (
                  <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:col-span-2">
                    <p className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      <SafeIcon icon={FiExternalLink} className="text-indigo-600" />
                      Shop link
                    </p>
                    <a
                      href={shopUrl}
                      className="mt-1 inline-flex max-w-full break-all text-sm font-black text-indigo-600 hover:text-indigo-700"
                    >
                      {shopUrl.replace('https://','')}
                    </a>
                  </div>
                )}
              </div>

              <button
                onClick={startConversation}
                className="mt-8 inline-flex w-fit items-center rounded-2xl bg-slate-900 px-7 py-4 text-xs font-black uppercase tracking-widest text-white transition-all hover:bg-slate-800"
              >
                <SafeIcon icon={FiMessageCircle} className="mr-3 text-indigo-300" />
                Message seller
              </button>
            </div>
          </div>
        </motion.section>

        <div className="mt-10 flex gap-2 overflow-x-auto border-b border-slate-200">
          <button
            onClick={()=>setActiveTab('menu')}
            className={`whitespace-nowrap px-6 py-4 text-xs font-black uppercase tracking-widest ${
              activeTab==='menu'
                ? 'border-b-4 border-indigo-600 text-indigo-600'
                : 'text-slate-400'
            }`}
          >
            <SafeIcon icon={FiShoppingBag} className="mr-2 inline" />
            Menu Items
          </button>

          <button
            onClick={()=>setActiveTab('reviews')}
            className={`whitespace-nowrap px-6 py-4 text-xs font-black uppercase tracking-widest ${
              activeTab==='reviews'
                ? 'border-b-4 border-indigo-600 text-indigo-600'
                : 'text-slate-400'
            }`}
          >
            <SafeIcon icon={FiStar} className="mr-2 inline" />
            Reviews ({sellerReviews.length})
          </button>

          <button
            onClick={()=>setActiveTab('policies')}
            className={`whitespace-nowrap px-6 py-4 text-xs font-black uppercase tracking-widest ${
              activeTab==='policies'
                ? 'border-b-4 border-indigo-600 text-indigo-600'
                : 'text-slate-400'
            }`}
          >
            <SafeIcon icon={FiShield} className="mr-2 inline" />
            Policies
          </button>
        </div>

        {activeTab==='menu' && (
          sellerOfferings.length
            ? (
              <section className="mt-10 grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 lg:grid-cols-3">
                {sellerOfferings.map((offering)=>(
                  <article
                    key={offering.id}
                    className="flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-lg shadow-slate-200/30"
                  >
                    <ImageWithFallback
                      src={offering.images?.[0]}
                      alt={offering.title}
                      className="h-56 w-full object-cover"
                      fallbackLabel="No product photo"
                    />

                    <div className="flex flex-1 flex-col p-7">
                      <SafeIcon
                        icon={FiShoppingBag}
                        className="mb-4 text-indigo-600"
                      />

                      <h3 className="text-xl font-black text-slate-900">
                        {offering.title}
                      </h3>

                      <p className="mt-3 flex-1 text-sm font-medium leading-relaxed text-slate-500">
                        {offering.description}
                      </p>

                      {offering.ingredients && (
                        <details className="mt-4 rounded-2xl border border-slate-100 bg-slate-50">
                          <summary className="cursor-pointer list-none px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Ingredients
                          </summary>
                          <p className="border-t border-slate-100 px-4 py-4 text-sm font-medium leading-relaxed text-slate-600">
                            {offering.ingredients}
                          </p>
                        </details>
                      )}

                      {formatAllergenLabels(offering.allergens).length > 0 && (
                        <div className="mt-4">
                          <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                            Allergen Information
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {formatAllergenLabels(offering.allergens).map((allergen) => (
                              <span
                                key={allergen}
                                className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-amber-800"
                              >
                                Contains {allergen}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-7 flex items-end justify-between gap-4">
                        <span className="text-lg font-black leading-tight text-indigo-600">
                          {getOfferingPriceLabel(offering)}
                        </span>

                        <button
                          onClick={()=>startOrder(offering)}
                          className="whitespace-nowrap rounded-xl bg-indigo-600 px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white"
                        >
                          Order
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </section>
            )
            : (
              <section className="mt-10 rounded-[2rem] border border-dashed border-indigo-200 bg-white px-6 py-20 text-center shadow-sm">
                <SafeIcon
                  icon={FiShoppingBag}
                  className="mx-auto mb-5 text-4xl text-indigo-300"
                />
                <h2 className="text-2xl font-black tracking-tight text-slate-900">
                  Delicious things coming soon...
                </h2>
                <p className="mt-3 font-medium text-slate-500">
                  This baker is still preparing their menu.
                </p>
              </section>
            )
        )}

        {activeTab==='reviews' && (
          <section className="mt-10 space-y-5">
            {sellerReviews.length
              ? sellerReviews.map((review)=>(
                <article
                  key={review.id}
                  className="rounded-[2rem] border border-slate-100 bg-white p-7 shadow-sm"
                >
                  <div className="flex items-center gap-1 text-amber-400">
                    {[1,2,3,4,5].map((star)=>(
                      <SafeIcon
                        key={star}
                        icon={FiStar}
                        className={
                          star<=review.rating
                            ? 'fill-current'
                            : 'text-slate-200'
                        }
                      />
                    ))}
                  </div>

                  <p className="mt-4 font-medium leading-relaxed text-slate-600">
                    {review.comment || 'No written comment.'}
                  </p>

                  <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {review.buyer_name || 'Verified Buyer'}
                    {' · '}
                    {review.created_at
                      ? format(new Date(review.created_at),'MMM d,yyyy')
                      : ''}
                  </p>
                </article>
              ))
              : (
                <div className="rounded-[2rem] bg-white p-16 text-center font-bold text-slate-400">
                  No reviews yet.
                </div>
              )}
          </section>
        )}

        {activeTab==='policies' && (
          <section className="mt-10">
            <PolicyDocument settings={seller.policy_settings} />
          </section>
        )}
      </div>

      {selectedOffering && (
        <OrderFormModal
          seller={seller}
          offerings={sellerOfferings}
          initialOffering={selectedOffering}
          onClose={()=>setSelectedOffering(null)}
        />
      )}
    </div>
  );
};

export default SellerProfilePage;
